/**
 * ╔══════════════════════════════════════════════════════════════════════════════════════════════╗
 * ║                              SCRAPER CONTROL DASHBOARD                                       ║
 * ║                    Advanced Control Panel for External Scraper Management                    ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════════════╝
 */

import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import {
  Activity,
  Server,
  AlertCircle,
  CheckCircle,
  Clock,
  Building2,
  TrendingUp,
  RefreshCw,
  Play,
  Square,
  Upload,
  Download,
  AlertTriangle,
  Cloud,
  Database,
  Cpu,
  MemoryStick,
  Network,
  Shield,
  Key,
  Trash2,
  GitBranch,
  Settings,
  BarChart3,
  LineChart,
  PieChart,
  ArrowUp,
  ArrowDown,
  Zap
} from 'lucide-react';
import { scraperLogger } from '../services/scraper-logging-service';
import { scraperMonitoringService } from '../lib/scraperMonitoringService';



// ============================================================================
// INTERFACES & TYPES
// ============================================================================

interface ScraperInfo {
  scraper_id: string;
  name: string;
  deployment_type: 'aws-ec2' | 'aws-lambda' | 'heroku' | 'other';
  status: 'active' | 'idle' | 'error' | 'offline' | 'maintenance' | 'deploying';
  last_heartbeat: string;
  last_run?: string;
  last_success?: string;
  error_count: number;
  total_runs: number;
  companies_scraped: number;
  avg_runtime: number;
  current_version?: string;
  deployment_id?: string;
  resource_usage: {
    cpu_percent: number;
    memory_mb: number;
    network_usage: number;
  };
  security_status: 'secure' | 'warning' | 'compromised';
  vpn_connected?: boolean;
}

interface DeploymentPackage {
  version: string;
  files: File[];
  config: Record<string, any>;
  rollback_version?: string;
}

interface PerformanceMetrics {
  timestamp: string;
  scraper_id: string;
  success_rate: number;
  avg_response_time: number;
  companies_per_hour: number;
  error_rate: number;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ScraperControlDashboard() {
  const [scrapers, setScrapers] = useState<ScraperInfo[]>([]);
  const [selectedScraper, setSelectedScraper] = useState<string | null>(null);
  const [performanceData, setPerformanceData] = useState<PerformanceMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [deploymentLoading, setDeploymentLoading] = useState<Set<string>>(new Set());
  const [operationLoading, setOperationLoading] = useState<Set<string>>(new Set());
  const [refreshInterval, setRefreshInterval] = useState(5000); // 5 seconds
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // ============================================================================
  // DATA LOADING
  // ============================================================================

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(loadDashboardData, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshInterval]);

  const loadDashboardData = async () => {
    try {
      const data = await scraperMonitoringService.getDashboardData();
      if (data && data.scrapers) {
        setScrapers(data.scrapers);
      }
      
      // Load performance data for metrics
      if (selectedScraper) {
        const metrics = await loadScraperMetrics(selectedScraper);
        setPerformanceData(metrics);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadScraperMetrics = async (scraperId: string): Promise<PerformanceMetrics[]> => {
    // Mock data - replace with actual API call
    const now = new Date();
    const metrics: PerformanceMetrics[] = [];
    
    for (let i = 23; i >= 0; i--) {
      const timestamp = new Date(now.getTime() - i * 60 * 60 * 1000);
      metrics.push({
        timestamp: timestamp.toISOString(),
        scraper_id: scraperId,
        success_rate: 85 + Math.random() * 10,
        avg_response_time: 1000 + Math.random() * 500,
        companies_per_hour: 20 + Math.random() * 30,
        error_rate: Math.random() * 5
      });
    }
    
    return metrics;
  };

  // ============================================================================
  // SCRAPER OPERATIONS
  // ============================================================================

  const startScraper = async (scraperId: string) => {
    setOperationLoading(prev => new Set(prev).add(scraperId));
    try {
      await scraperMonitoringService.sendScraperCommand(scraperId, 'start');
      await scraperLogger.logScraperOperation(scraperId, 'start', {
        initiated_by: 'control_dashboard',
        timestamp: new Date().toISOString()
      });
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to start scraper:', error);
      alert('Failed to start scraper. Check logs for details.');
    } finally {
      setOperationLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(scraperId);
        return newSet;
      });
    }
  };

  const stopScraper = async (scraperId: string) => {
    setOperationLoading(prev => new Set(prev).add(scraperId));
    try {
      await scraperMonitoringService.sendScraperCommand(scraperId, 'stop');
      await scraperLogger.logScraperOperation(scraperId, 'stop', {
        initiated_by: 'control_dashboard',
        timestamp: new Date().toISOString()
      });
      await loadDashboardData();
    } catch (error) {
      console.error('Failed to stop scraper:', error);
      alert('Failed to stop scraper. Check logs for details.');
    } finally {
      setOperationLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(scraperId);
        return newSet;
      });
    }
  };

  const deployScraper = async (scraperId: string, deploymentPackage?: DeploymentPackage) => {
    setDeploymentLoading(prev => new Set(prev).add(scraperId));
    try {
      const deploymentId = `deploy-${Date.now()}`;
      
      // Log deployment start
      await scraperLogger.logDeployment({
        scraper_id: scraperId,
        deployment_id: deploymentId,
        version: deploymentPackage?.version || 'latest',
        deployment_type: 'full_deployment',
        initiated_by: 'control_dashboard',
        deployment_notes: 'Deployed via control dashboard'
      });

      // Send deployment command
      await scraperMonitoringService.deployScraper(scraperId, deploymentPackage);
      
      await loadDashboardData();
      alert('Deployment initiated successfully!');
    } catch (error) {
      console.error('Failed to deploy scraper:', error);
      alert('Failed to deploy scraper. Check logs for details.');
    } finally {
      setDeploymentLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(scraperId);
        return newSet;
      });
    }
  };

  const undeployScraper = async (scraperId: string) => {
    if (!confirm(`Are you sure you want to undeploy scraper ${scraperId}? This will stop and remove the scraper instance.`)) {
      return;
    }

    setDeploymentLoading(prev => new Set(prev).add(scraperId));
    try {
      await scraperMonitoringService.sendScraperCommand(scraperId, 'undeploy');
      await scraperLogger.logScraperOperation(scraperId, 'stop', {
        initiated_by: 'control_dashboard',
        action: 'undeploy',
        timestamp: new Date().toISOString()
      });
      await loadDashboardData();
      alert('Scraper undeployed successfully!');
    } catch (error) {
      console.error('Failed to undeploy scraper:', error);
      alert('Failed to undeploy scraper. Check logs for details.');
    } finally {
      setDeploymentLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(scraperId);
        return newSet;
      });
    }
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'idle': return 'bg-blue-100 text-blue-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'offline': return 'bg-gray-100 text-gray-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'deploying': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getDeploymentTypeIcon = (type: string) => {
    switch (type) {
      case 'aws-ec2': return <Server className="w-4 h-4" />;
      case 'aws-lambda': return <Zap className="w-4 h-4" />;
      case 'heroku': return <Cloud className="w-4 h-4" />;
      default: return <Database className="w-4 h-4" />;
    }
  };

  const getSecurityStatusIcon = (status: string) => {
    switch (status) {
      case 'secure': return <Shield className="w-4 h-4 text-green-600" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'compromised': return <AlertCircle className="w-4 h-4 text-red-600" />;
      default: return <Shield className="w-4 h-4 text-gray-600" />;
    }
  };

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading scraper control dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 p-6">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <Card className="mb-8 shadow-lg border-0">
          <CardHeader className="pb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Settings className="w-8 h-8 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                    Scraper Control Dashboard
                  </CardTitle>
                  <p className="text-gray-600">
                    Advanced control panel for external scraper management and deployment
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Button 
                  variant="outline"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="hover:bg-blue-50"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  {showAdvanced ? 'Simple View' : 'Advanced View'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={loadDashboardData}
                  className="hover:bg-green-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="shadow-lg border-0 bg-gradient-to-br from-green-50 to-emerald-50">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">
                {scrapers.filter(s => s.status === 'active').length}
              </div>
              <div className="text-green-700 font-medium">Active Scrapers</div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg border-0 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {scrapers.reduce((sum, s) => sum + s.total_runs, 0)}
              </div>
              <div className="text-blue-700 font-medium">Total Runs</div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg border-0 bg-gradient-to-br from-purple-50 to-violet-50">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {scrapers.reduce((sum, s) => sum + s.companies_scraped, 0)}
              </div>
              <div className="text-purple-700 font-medium">Companies Scraped</div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg border-0 bg-gradient-to-br from-orange-50 to-amber-50">
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-orange-600 mb-2">
                {scrapers.filter(s => s.error_count > 0).length}
              </div>
              <div className="text-orange-700 font-medium">Scrapers with Errors</div>
            </CardContent>
          </Card>
        </div>

        {/* Performance Metrics Section */}
        {showAdvanced && selectedScraper && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LineChart className="w-5 h-5 text-blue-600" />
                  Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">94.2%</div>
                    <div className="text-sm text-green-700">Success Rate</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">2.1%</div>
                    <div className="text-sm text-red-700">Error Rate</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">1.2s</div>
                    <div className="text-sm text-blue-700">Avg Response</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">45</div>
                    <div className="text-sm text-purple-700">Companies/Hr</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Last successful run</span>
                    <span className="text-sm font-medium text-green-600">2 min ago</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Companies processed today</span>
                    <span className="text-sm font-medium text-blue-600">1,247</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Uptime today</span>
                    <span className="text-sm font-medium text-purple-600">97.8%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Next scheduled run</span>
                    <span className="text-sm font-medium text-orange-600">15 min</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Scrapers Grid */}
        <div className="grid gap-6">
          {scrapers.map((scraper) => (
            <Card 
              key={scraper.scraper_id} 
              className="shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => setSelectedScraper(scraper.scraper_id)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      {getDeploymentTypeIcon(scraper.deployment_type)}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">{scraper.name}</h3>
                      <p className="text-sm text-gray-500">ID: {scraper.scraper_id}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Badge className={getStatusColor(scraper.status)}>
                      {scraper.status}
                    </Badge>
                    {scraper.vpn_connected && (
                      <Badge className="bg-blue-100 text-blue-800">
                        <Shield className="w-3 h-3 mr-1" />
                        VPN
                      </Badge>
                    )}
                    {getSecurityStatusIcon(scraper.security_status)}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{scraper.companies_scraped}</div>
                    <div className="text-xs text-gray-500">Companies</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{scraper.total_runs}</div>
                    <div className="text-xs text-gray-500">Total Runs</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{scraper.error_count}</div>
                    <div className="text-xs text-gray-500">Errors</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{scraper.avg_runtime}s</div>
                    <div className="text-xs text-gray-500">Avg Runtime</div>
                  </div>
                </div>

                {showAdvanced && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Cpu className="w-4 h-4 text-blue-600" />
                      <span className="text-sm">CPU: {scraper.resource_usage.cpu_percent}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MemoryStick className="w-4 h-4 text-green-600" />
                      <span className="text-sm">Memory: {scraper.resource_usage.memory_mb}MB</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Network className="w-4 h-4 text-purple-600" />
                      <span className="text-sm">Network: {scraper.resource_usage.network_usage}KB/s</span>
                    </div>
                  </div>
                )}

                {/* Control Actions */}
                <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      startScraper(scraper.scraper_id);
                    }}
                    disabled={operationLoading.has(scraper.scraper_id) || scraper.status === 'active'}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Start
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      stopScraper(scraper.scraper_id);
                    }}
                    disabled={operationLoading.has(scraper.scraper_id) || scraper.status === 'offline'}
                  >
                    <Square className="w-4 h-4 mr-1" />
                    Stop
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      deployScraper(scraper.scraper_id);
                    }}
                    disabled={deploymentLoading.has(scraper.scraper_id)}
                    className="hover:bg-blue-50"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    {deploymentLoading.has(scraper.scraper_id) ? 'Deploying...' : 'Deploy'}
                  </Button>

                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      undeployScraper(scraper.scraper_id);
                    }}
                    disabled={deploymentLoading.has(scraper.scraper_id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Undeploy
                  </Button>

                  {scraper.current_version && (
                    <Badge variant="outline" className="ml-auto">
                      v{scraper.current_version}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Status Distribution */}
        {showAdvanced && scrapers.length > 0 && (
          <Card className="mt-8 shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5 text-indigo-600" />
                Scraper Status Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">
                    {scrapers.filter(s => s.status === 'active').length}
                  </div>
                  <div className="text-sm text-green-700">Active</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">
                    {scrapers.filter(s => s.status === 'idle').length}
                  </div>
                  <div className="text-sm text-blue-700">Idle</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-600">
                    {scrapers.filter(s => s.status === 'error').length}
                  </div>
                  <div className="text-sm text-red-700">Error</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-600">
                    {scrapers.filter(s => s.status === 'offline').length}
                  </div>
                  <div className="text-sm text-gray-700">Offline</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}